import type { ReactNode } from "react";
import { CustomCellDate, CrudLink } from "@/shared/components";
import { Chip, Stack, Typography } from "@mui/material";
import type { UsersListRow } from "@/admin/entities/users";
import {
  getOrganizationRoleLabel,
  type UserOrganizationsListRow,
} from "@/admin/entities/organizationUsers";
import type { UserRoleInfo, UserPlatformInfo } from "./types";
import type { UserRow } from "./types";
import type { DataListViewColumns } from "@/shared/components/DataListView";

/**
 * Конфигурация UI для feature управления пользователями
 */

// Конфигурация колонок таблицы
export const getColumnsConfig = () => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "name",
    label: "Имя",
    options: {
      customBodyRender: (data: unknown) => {
        const user = data as UsersListRow;
        return (
          <CrudLink id={user.id} tab="details">
            <div>
              <div>{user.displayName}</div>
              <Typography variant="body2" color="textSecondary">
                {user.email || user.phone}
              </Typography>
            </div>
          </CrudLink>
        );
      },
    },
  },
  {
    name: "phone",
    label: "Телефон",
    options: {
      customBodyRender: (phone: unknown) => {
        if (!phone) return "—";
        return phone as string;
      },
    },
  },
  {
    name: "role",
    label: "Роль",
    options: {
      customBodyRender: (value: unknown) => {
        const roleData = value as UserRoleInfo;
        const chips: ReactNode[] = [];

        if (roleData?.isSuperAdmin) {
          chips.push(
            <Chip
              key="superAdmin"
              label="Суперадмин"
              color="error"
              size="small"
              variant="filled"
            />
          );
        } else if (roleData?.isAdmin) {
          chips.push(
            <Chip
              key="admin"
              label="Админ"
              color="primary"
              size="small"
              variant="filled"
            />
          );
        }
        // isAdmin/isPartner могут быть выставлены одновременно на одной
        // служебной записи — показываем оба независимо друг от друга.
        if (roleData?.isPartner) {
          chips.push(
            <Chip
              key="partner"
              label="Партнёр"
              color="secondary"
              size="small"
              variant="filled"
            />
          );
        }
        if (roleData?.isPlatformAdmin) {
          chips.push(
            <Chip
              key="platformAdmin"
              label="Модератор платформы"
              color="default"
              size="small"
              variant="outlined"
            />
          );
        }

        if (!chips.length) return null;
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {chips}
          </Stack>
        );
      },
    },
  },
  {
    name: "platform",
    label: "Платформа",
    options: {
      customBodyRender: (value: unknown) => {
        const { platform } = value as UserPlatformInfo;
        if (!platform) return "—";
        return (
          <CrudLink id={platform.id} tab="details" path="/admin/platforms">
            {platform.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "createdAt",
    label: "Дата создания",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
];

// Конфигурация текстов интерфейса
export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Пользователи",
  itemCreated: "Пользователь создан",
  itemUpdated: "Пользователь обновлен",
  createItem: "Создать пользователя",
  refetchList: "Обновить список",
  deleteItems: "Удалить пользователей",
  itemsDeleted: "Пользователи удалены",
  itemsRestored: "Пользователи восстановлены",
  restoreItems: "Восстановить пользователей",
  cancel: "Отмена",
  editItem: "Пользователь",
  showTitle: "Детали",
});

// Колонки вкладки "Организации" (где юзер сотрудник) на странице юзера
export const getUserOrganizationsColumnsConfig = () => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "organization",
    label: "Организация",
    options: {
      customBodyRender: (data: unknown) => {
        const { organization } = data as UserOrganizationsListRow;
        return (
          <CrudLink
            id={organization.id}
            tab="details"
            path="/admin/organizations"
          >
            {organization.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "roles",
    label: "Роли",
    options: {
      customBodyRender: (data: unknown) => {
        const { roles } = data as UserOrganizationsListRow;
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {roles.map((role) => (
              <Chip
                key={role}
                label={getOrganizationRoleLabel(role)}
                size="small"
              />
            ))}
          </Stack>
        );
      },
    },
  },
  {
    name: "createdAt",
    label: "Дата вступления",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
];

// Заголовок рисует сам UserDetails — listTitle: undefined перекрывает
// дефолт InnerCRUD ("Items list"), чтобы не продублировать его своим
// <Typography variant="h5">.
export const getUserOrganizationsTextsConfig = () => ({
  listTitle: undefined,
});

// Колонки для мобильного представления: имя с контактом и роль
export const getMobileColumnsConfig = (): DataListViewColumns<UserRow> => ({
  main: {
    title: "Пользователь",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack sx={{ minWidth: 0 }}>
        <CrudLink id={row.id} tab="details" noWrap>
          {row.displayName}
        </CrudLink>
        <Typography variant="body2" color="textSecondary" noWrap>
          {row.email || row.phone || "—"}
        </Typography>
      </Stack>
    ),
  },
  role: {
    title: "Роль",
    sx: { width: 108, flexShrink: 0 },
    renderCell: (row) => {
      if (row.isSuperAdmin) {
        return <Chip label="Суперадмин" color="error" size="small" />;
      }
      if (row.isAdmin) {
        return <Chip label="Админ" color="primary" size="small" />;
      }
      if (row.isPartner) {
        return <Chip label="Партнёр" color="secondary" size="small" />;
      }
      return (
        <Typography variant="body2" color="textSecondary">
          Пользователь
        </Typography>
      );
    },
  },
});
